import { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Divider,
  Modal,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Header } from "../Header";
import {
  Container,
  Content,
  IconWrapper,
  StyledButtonDelete,
  StyledButtonEdit,
  StyledDate,
  StyledDividerRow,
  StyledDividerTd,
  StyledSendButton,
  StyledTd,
  StyledTdActions,
  StyledTh,
  StyledTr,
  TableHeader,
  Title,
} from "./style";
import { Loading } from "../Loading";
import { getSessionUser } from "../../utils/users";
import { IUser } from "../Login/ILogin";
import { apiOperation, headers } from "../../services/api";
import { Resultado } from "../../models/base/basic";
import { Bloqueio } from "../../models/bloqueio";
import { Error } from "../Error";

let usuarioLogado: IUser | null = null;

const emptyGuid = "00000000-0000-0000-0000-000000000000";

const editInicial: Bloqueio = {
  bloqueioId: emptyGuid,
  tipoBloqueio: "",
  valor: "",
  acao: "",
  nomeUsuarioInclusao: "",
  dataInclusao: new Date(),
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "4px",
};

const ufs = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

const regioes = ["Norte", "Nordeste", "Centro-Oeste", "Sudeste", "Sul"];

const acoes : Map<string, string> = new Map([
  ["Bloqueio", "Bloqueio"],
  ["Restricao", "Restrição"]
]);

const tiposBloqueio : Map<string, string> = new Map([
  ["Documento", "Documento"],
  ["CNAE", "CNAE"],
  ["UF", "UF"],
  ["Regiao", "Região"]
]);


export const BlockUser = () => {
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [messagesLoading, setMessagesLoading] = useState<string>("");
  const [blockList, setBlockList] = useState<Bloqueio[]>([]);
  const [edicaoId, setEdicaoId] = useState<string | null>("");
  const [edicao, setEdicao] = useState<Bloqueio>(editInicial);
  const [open, setOpen] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [errorApi, setErrorApi] = useState({
    message: "",
    title: "",
    state: false,
  });

  useEffect(() => {
    usuarioLogado = getSessionUser();
    carregarBlockList();
  }, []);

  async function carregarBlockList() {
    setLoadingModal(true);
    try {
      const urlString = `bloqueio`;
      const config = { headers: headers() };
      const { data } = await apiOperation.get<Bloqueio[]>(urlString, config);
      setBlockList(data);
    } catch (error: any) {
      setErrorApi({
        title: `API Não disponível`,
        message: `Falha ao buscar bloqueios!`,
        state: true,
      });
    } finally {
      setLoadingModal(false);
    }
  }

  function cadastrar() {
    const newItem = structuredClone(editInicial);
    editar(newItem);
  }

  async function excluir(id: string) {
    try {
      setLoadingModal(true);
      const urlString = `bloqueio/${id}`;
      const config = { headers: headers() };
      await apiOperation.delete(urlString, config);
    } catch (error: any) {
      setErrorApi({
        title: `API Não disponível`,
        message: `Falha ao excluir!`,
        state: true,
      });
    } finally {
      carregarBlockList();
      setLoadingModal(false);
    }
  }

  function editar(valor: Bloqueio) {
    setOpen(true);
    setEdicao(valor);
    setEdicaoId(valor.bloqueioId);
    setIsValid(null); // Resetar validação ao abrir
    // Pré-valida o valor existente ao editar
    if (valor.bloqueioId !== emptyGuid) {
      handleValueChange(valor.valor, valor.tipoBloqueio);
    }
  }

  function fecharModal() {
    setOpen(false);
    if (edicao.bloqueioId === emptyGuid && !edicao.valor) {
      setBlockList(blockList.filter((item) => item.bloqueioId !== edicaoId));
    }
    cancelar();
  }

  function cancelar() {
    setOpen(false);
    setEdicaoId(null);
    setEdicao(editInicial);
  }

  async function salvar() {
    if (!isValid) {
      alert("Valor inválido. Por favor, verifique.");
      return;
    }

    try {
      setLoadingModal(true);

      const payload = { ...edicao };

      if (
        payload.tipoBloqueio === "Documento" ||
        payload.tipoBloqueio === "CNAE"
      ) {
        payload.valor = payload.valor.replace(/\D/g, "");
      }

      if (edicao.bloqueioId === emptyGuid && usuarioLogado) {
        payload.nomeUsuarioInclusao = usuarioLogado.login;
      }

      const urlString = `bloqueio`;
      const config = { headers: headers() };

      let data: Resultado<boolean> | undefined = undefined;

      if (edicao.bloqueioId === emptyGuid) {
        const response = await apiOperation.post<Resultado<boolean>>(
          urlString,
          payload,
          config
        );
        data = response.data;
      } else {
        const response = await apiOperation.put<Resultado<boolean>>(
          urlString,
          payload,
          config
        );
        data = response.data;
      }

      if (data?.erro) {
        const erros = data.erros.map((erro) => erro).join(", ");
        setErrorApi({ title: `Erro ao salvar`, message: erros, state: true });
      }

      setOpen(false);
    } catch (error: any) {
      setErrorApi({
        title: `API Não disponível`,
        message: `Falha ao salvar bloqueio!`,
        state: true,
      });
    } finally {
      carregarBlockList();
      setLoadingModal(false);
      setEdicaoId(null);
    }
  }

  // --- MÁSCARAS E VALIDAÇÕES ---

  const applyDocumentMask = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 11) {
      // CPF
      return digits
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    } else {
      // CNPJ
      return digits
        .replace(/^(\d{2})(\d)/, "$1.$2")
        .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
        .replace(/\.(\d{3})(\d)/, ".$1/$2")
        .replace(/(\d{4})(\d)/, "$1-$2");
    }
  };

  const validateCPF = (cpf: string): boolean => {
    const digits = cpf.replace(/\D/g, "");
    if (digits.length !== 11 || /^(\d)\1+$/.test(digits)) return false;
    let sum = 0,
      remainder;
    for (let i = 1; i <= 9; i++)
      sum += parseInt(digits.substring(i - 1, i)) * (11 - i);
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(digits.substring(9, 10))) return false;
    sum = 0;
    for (let i = 1; i <= 10; i++)
      sum += parseInt(digits.substring(i - 1, i)) * (12 - i);
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(digits.substring(10, 11))) return false;
    return true;
  };

  const validateCNPJ = (cnpj: string): boolean => {
    const digits = cnpj.replace(/\D/g, "");
    if (digits.length !== 14 || /^(\d)\1+$/.test(digits)) return false;
    let size = digits.length - 2;
    let numbers = digits.substring(0, size);
    const verifiers = digits.substring(size);
    let sum = 0;
    let pos = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(verifiers.charAt(0))) return false;
    size += 1;
    numbers = digits.substring(0, size);
    sum = 0;
    pos = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(verifiers.charAt(1))) return false;
    return true;
  };

  const validateCNAE = (cnae: string): boolean => {
    return cnae.length === 7;
  };

  const handleValueChange = (value: string, type: string) => {
    let maskedValue = value;
    let digitsOnly = value.replace(/\D/g, "");

    switch (type) {
      case "Documento":
        maskedValue = applyDocumentMask(digitsOnly);
        if (digitsOnly.length === 11) setIsValid(validateCPF(digitsOnly));
        else if (digitsOnly.length === 14) setIsValid(validateCNPJ(digitsOnly));
        else setIsValid(null);
        setEdicao((prev) => ({ ...prev, valor: maskedValue }));
        break;
      case "CNAE":
        const cnaeValue = digitsOnly.slice(0, 7);

        setIsValid(validateCNAE(cnaeValue));
        setEdicao((prev) => ({ ...prev, valor: cnaeValue }));
        break;
      case "UF":
      case "Regiao":
        maskedValue = value;
        setIsValid(!!value); // Válido se não for vazio
        setEdicao((prev) => ({ ...prev, valor: maskedValue }));
        break;
      default:
        setIsValid(false);
    }
  };

  const handleTypeChange = (type: string) => {
    setEdicao({ ...edicao, tipoBloqueio: type, valor: "" }); // Limpa o valor ao trocar o tipo
    setIsValid(null); // Reseta a validação
  };

  function renderInputField() {
    const { tipoBloqueio, valor } = edicao;

    switch (tipoBloqueio) {
      case "Documento":
        return (
          <TextField
            fullWidth
            placeholder="Digite o CPF ou CNPJ"
            value={valor}
            onChange={(e) => handleValueChange(e.target.value, "Documento")}
            error={isValid === false}
            helperText={
              isValid === false
                ? "Documento inválido"
                : isValid === true
                ? "Documento válido"
                : ""
            }
            disabled={edicaoId !== emptyGuid}
            sx={{ mb: 1 }}
          />
        );
      case "CNAE":
        return (
          <TextField
            fullWidth
            placeholder="Digite os 7 dígitos do CNAE"
            value={valor}
            onChange={(e) => handleValueChange(e.target.value, "CNAE")}
            error={isValid === false}
            helperText={
              isValid === false
                ? "O CNAE deve ter 7 dígitos"
                : isValid === true
                ? "CNAE válido"
                : ""
            }
            disabled={edicaoId !== emptyGuid}
            sx={{ mb: 1 }}
          />
        );
      case "UF":
        return (
          <FormControl fullWidth sx={{ mb: 1 }}>
            <Select
              value={valor}
              onChange={(e) => handleValueChange(e.target.value, "UF")}
              displayEmpty
              disabled={edicaoId !== emptyGuid}
            >
              <MenuItem value="" disabled>
                Selecione a UF
              </MenuItem>
              {ufs.map((uf) => (
                <MenuItem key={uf} value={uf}>
                  {uf}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "Regiao":
        return (
          <FormControl fullWidth sx={{ mb: 1 }}>
            <Select
              value={valor}
              onChange={(e) => handleValueChange(e.target.value, "Regiao")}
              displayEmpty
              disabled={edicaoId !== emptyGuid}
            >
              <MenuItem value="" disabled>
                Selecione a Região
              </MenuItem>
              {regioes.map((regiao) => (
                <MenuItem key={regiao} value={regiao}>
                  {regiao}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      default:
        return null;
    }
  }

  function mapList(list: Bloqueio[]) {
    return list.map((item, index) => {
      const showDivider = index !== list.length - 1;

      return (
        <>
          <StyledTr key={item.bloqueioId}>
            <StyledTd>{tiposBloqueio.get(item.tipoBloqueio)}</StyledTd>
            <StyledTd>{item.valor}</StyledTd>
            <StyledTd>{acoes.get(item.acao)}</StyledTd>
            <StyledTd>{item.nomeUsuarioInclusao}</StyledTd>
            <StyledTd>
              <StyledDate>
                {new Date(item.dataInclusao).toLocaleString()}
              </StyledDate>
            </StyledTd>
            <StyledTdActions>
              <StyledButtonEdit
                onClick={() => editar(item)}
                variant="contained"
                color="primary"
              >
                Editar
              </StyledButtonEdit>
              <StyledButtonDelete
                onClick={() => excluir(item.bloqueioId)}
                variant="contained"
                color="error"
              >
                Excluir
              </StyledButtonDelete>
            </StyledTdActions>
          </StyledTr>

          {showDivider && (
            <StyledDividerRow>
              <StyledDividerTd colSpan={6}>
                {" "}
                {/* Aumentado para 6 colunas */}
                <Divider />
              </StyledDividerTd>
            </StyledDividerRow>
          )}
        </>
      );
    });
  }

  return (
    <>
      <Modal open={open} onClose={fecharModal} aria-labelledby="modal-title">
        <Box sx={modalStyle}>
          <Typography
            id="modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
          >
            {edicao.bloqueioId === emptyGuid
              ? "Cadastrar Bloqueio"
              : "Editar Bloqueio"}
          </Typography>

          {/* NOVO DROPDOWN TIPO DE BLOQUEIO */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Tipo de Bloqueio
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              value={edicao.tipoBloqueio}
              onChange={(e) => handleTypeChange(e.target.value)}
              displayEmpty
              disabled={edicaoId !== emptyGuid}
            >
              <MenuItem value="" disabled>
                Selecione o Tipo
              </MenuItem>
              <MenuItem value="Documento">Documento</MenuItem>
              <MenuItem value="CNAE">CNAE</MenuItem>
              <MenuItem value="UF">UF</MenuItem>
              <MenuItem value="Regiao">Região</MenuItem>
            </Select>
          </FormControl>

          {edicao.tipoBloqueio && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Valor
              </Typography>
              {renderInputField()}
            </>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Ação
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              value={edicao.acao}
              onChange={(e) => setEdicao({ ...edicao, acao: e.target.value })}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Selecione
              </MenuItem>
              <MenuItem value="Bloqueio">Bloqueio</MenuItem>
              <MenuItem value="Restricao">Restrição</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <StyledSendButton
              variant="contained"
              onClick={salvar}
              disabled={!isValid || !edicao.acao || !edicao.tipoBloqueio}
            >
              <IconWrapper>▶</IconWrapper> ENVIAR
            </StyledSendButton>
          </Box>
        </Box>
      </Modal>

      <Header />
      <Loading
        mode="blurry"
        hidden={!loadingModal}
        messages={messagesLoading}
      />
      <Container>
        <Content>
          <Title>Lista de Bloqueio</Title>
          <Button
            onClick={cadastrar}
            className={"buttonPesquisa"}
            variant="contained"
            color="secondary"
            sx={{ width: "10.4em" }}
          >
            Cadastrar
          </Button>
        </Content>

        <Card sx={{ minWidth: 275, placeItems: "center" }}>
          <table style={{ width: "100%", height: "100%", padding: "10px" }}>
            <TableHeader>
              <tr>
                {/* COLUNAS DA TABELA ATUALIZADAS */}
                <StyledTh>Tipo de Bloqueio</StyledTh>
                <StyledTh>Valor</StyledTh>
                <StyledTh>Ação</StyledTh>
                <StyledTh>Solicitante</StyledTh>
                <StyledTh>Data do Bloqueio</StyledTh>
                <StyledTh>Ações</StyledTh>
              </tr>
            </TableHeader>
            <tbody>{mapList(blockList)}</tbody>
          </table>
        </Card>
        <Error
          hasOpen={errorApi.state}
          handleClose={() => setErrorApi({ ...errorApi, state: false })}
          errorMsg={errorApi.message}
          title={errorApi.title}
        />
      </Container>
    </>
  );
};
